/**
 * Timezone helpers for analytics date ranges.
 *
 * The analytics UI lets you pick a wall-clock window ("8/21 00:00 – 8/21 23:59")
 * *and* the timezone that window is measured in. Everything crossing the network
 * is an absolute instant (ISO 8601 with an offset); the timezone only decides how
 * a wall-clock string maps onto an instant, and how an instant is displayed back.
 *
 * Why this exists: `new Date("2026-08-21T00:00")` resolves in the *browser's*
 * timezone, which silently made analytics links mean different windows on
 * different machines. These helpers make the zone explicit at both ends.
 */

/** IANA zone GA4 shards its `events_YYYYMMDD` tables on — see FIREBASE_ANALYTICS_TIMEZONE. */
export const ANALYTICS_TIMEZONE = 'America/Los_Angeles';

/** The viewer's own timezone, used as the default for ad-hoc lookups. */
export const getBrowserTimeZone = () =>
  Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

// Fallback for browsers without Intl.supportedValuesOf (pre-Chrome 99 / Safari 15.4).
const FALLBACK_ZONES = [
  'UTC',
  'America/Los_Angeles', 'America/Denver', 'America/Chicago', 'America/New_York',
  'America/Anchorage', 'Pacific/Honolulu', 'America/Toronto', 'America/Vancouver',
  'America/Mexico_City', 'America/Sao_Paulo',
  'Europe/London', 'Europe/Dublin', 'Europe/Paris', 'Europe/Berlin', 'Europe/Madrid',
  'Europe/Rome', 'Europe/Zurich', 'Europe/Stockholm', 'Europe/Moscow',
  'Asia/Dubai', 'Asia/Kolkata', 'Asia/Bangkok', 'Asia/Shanghai', 'Asia/Tokyo',
  'Asia/Seoul', 'Asia/Singapore', 'Australia/Sydney', 'Australia/Perth',
  'Pacific/Auckland',
];

let cachedZones = null;

/**
 * Every IANA timezone the browser knows about, with the analytics zone and the
 * viewer's own zone hoisted to the top so the two common picks are one click away.
 * @returns {string[]}
 */
export const getTimeZoneList = () => {
  if (cachedZones) return cachedZones;

  let zones;
  try {
    zones = typeof Intl.supportedValuesOf === 'function'
      ? Intl.supportedValuesOf('timeZone')
      : FALLBACK_ZONES;
  } catch {
    zones = FALLBACK_ZONES;
  }

  const pinned = [...new Set([ANALYTICS_TIMEZONE, getBrowserTimeZone(), 'UTC'])];
  const rest = zones.filter((z) => !pinned.includes(z)).sort();
  cachedZones = [...pinned, ...rest];
  return cachedZones;
};

/**
 * How far `timeZone` is from UTC at a given instant, in milliseconds.
 * Derived by formatting the instant in the zone and reading the wall clock back,
 * so it accounts for DST without a lookup table.
 * @param {Date} date
 * @param {string} timeZone
 * @returns {number} offset in ms (positive east of UTC)
 */
const offsetMsAt = (date, timeZone) => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).formatToParts(date).reduce((acc, p) => {
    if (p.type !== 'literal') acc[p.type] = p.value;
    return acc;
  }, {});

  const asUTC = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour) % 24, // Intl can emit "24" for midnight
    Number(parts.minute),
    Number(parts.second)
  );
  return asUTC - date.getTime();
};

/**
 * Resolve a `YYYY-MM-DDTHH:mm` wall-clock string *in a given zone* to an instant.
 *
 * Run twice because the offset depends on the instant we're still solving for —
 * the first pass gets within an hour, the second settles it across DST edges.
 * @param {string} wallTime - e.g. "2026-08-21T00:00"
 * @param {string} timeZone - IANA zone the wall time is measured in
 * @returns {Date|null} null if the string can't be parsed
 */
export const wallTimeToInstant = (wallTime, timeZone) => {
  if (typeof wallTime !== 'string') return null;
  const match = wallTime.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/);
  if (!match) return null;

  const [, y, mo, d, h, mi, sec] = match;
  const naive = Date.UTC(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi), Number(sec || 0));

  let instant = new Date(naive - offsetMsAt(new Date(naive), timeZone));
  instant = new Date(naive - offsetMsAt(instant, timeZone));
  return isNaN(instant.getTime()) ? null : instant;
};

/**
 * Render an instant as a `YYYY-MM-DDTHH:mm` wall-clock string in a zone —
 * the format a `<input type="datetime-local">` expects.
 * @param {Date|string|number} date
 * @param {string} timeZone
 * @returns {string} empty string if the date is invalid
 */
export const instantToWallTime = (date, timeZone) => {
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return '';

  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    hour12: false,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  }).formatToParts(d).reduce((acc, p) => {
    if (p.type !== 'literal') acc[p.type] = p.value;
    return acc;
  }, {});

  const hour = String(Number(parts.hour) % 24).padStart(2, '0');
  return `${parts.year}-${parts.month}-${parts.day}T${hour}:${parts.minute}`;
};

/**
 * Format an instant for display in a zone.
 * @param {Date|string|number} date
 * @param {string} timeZone
 * @param {Intl.DateTimeFormatOptions} [options]
 * @returns {string}
 */
export const formatInTimeZone = (date, timeZone, options = {}) => {
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return 'Unknown';
  return new Intl.DateTimeFormat('en-US', { timeZone, ...options }).format(d);
};

/**
 * ISO 8601 timestamp with millisecond precision, written as the wall clock in a
 * zone plus that zone's offset — "2026-09-22T20:00:30.687-07:00". UTC renders as
 * "...Z". Sorts correctly within one offset and parses back to the same instant.
 * @param {Date|number} date
 * @param {string} timeZone
 * @returns {string} empty string if the date is invalid
 */
export const formatIsoInTimeZone = (date, timeZone) => {
  const ms = date instanceof Date ? date.getTime() : Number(date);
  if (!Number.isFinite(ms)) return '';

  // offsetMsAt reads the wall clock at whole-second precision, so measure it on a
  // whole-second instant and round to minutes; otherwise the ms fraction leaks in.
  const offsetMs = Math.round(offsetMsAt(new Date(Math.floor(ms / 1000) * 1000), timeZone) / 60000) * 60000;
  const wall = new Date(ms + offsetMs).toISOString().slice(0, -1); // drop the "Z"
  if (offsetMs === 0) return `${wall}Z`;

  const sign = offsetMs > 0 ? '+' : '-';
  const abs = Math.abs(offsetMs) / 60000;
  const hh = String(Math.floor(abs / 60)).padStart(2, '0');
  const mm = String(abs % 60).padStart(2, '0');
  return `${wall}${sign}${hh}:${mm}`;
};

/**
 * Short zone label for a given instant — "PDT", "GMT+5:30".
 * Takes an instant because the abbreviation changes across DST.
 * @param {string} timeZone
 * @param {Date} [date]
 * @returns {string}
 */
export const timeZoneAbbrev = (timeZone, date = new Date()) => {
  const part = new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'short' })
    .formatToParts(date)
    .find((p) => p.type === 'timeZoneName');
  return part ? part.value : timeZone;
};

/** Human-readable option label: "America/Los_Angeles (PDT)". */
export const timeZoneLabel = (timeZone, date = new Date()) =>
  `${timeZone.replace(/_/g, ' ')} (${timeZoneAbbrev(timeZone, date)})`;
